export class JsonFormatter {
  static format(jsonString: string, indentSize: number = 2): string {
    try {
      // First, parse to validate and normalize
      const parsed = JSON.parse(jsonString)
      
      // Format with specified indentation
      return JSON.stringify(parsed, null, indentSize)
    } catch (error) {
      // If parsing fails, try to fix common issues and retry
      const fixed = this.attemptToFix(jsonString)
      if (fixed) {
        try {
          const parsed = JSON.parse(fixed)
          return JSON.stringify(parsed, null, indentSize)
        } catch {
          // If still fails, return original with basic formatting attempt
          return this.basicFormat(jsonString, indentSize)
        }
      }
      
      // Return original if we can't fix it
      return jsonString
    }
  }

  static minify(jsonString: string): string {
    try {
      const parsed = JSON.parse(jsonString)
      return JSON.stringify(parsed)
    } catch (error) {
      // If parsing fails, try to fix and retry
      const fixed = this.attemptToFix(jsonString)
      if (fixed) {
        try {
          const parsed = JSON.parse(fixed)
          return JSON.stringify(parsed)
        } catch {
          // Return original if we can't fix it
          return jsonString
        }
      }
      return jsonString
    }
  }

  static normalizeIndentation(jsonString: string, indentSize: number = 2): string {
    try {
      // First try to parse and re-stringify to normalize
      const parsed = JSON.parse(jsonString)
      return JSON.stringify(parsed, null, indentSize)
    } catch {
      // If parsing fails, try to fix mixed indentation manually
      return this.fixMixedIndentation(jsonString, indentSize)
    }
  }

  private static fixMixedIndentation(jsonString: string, indentSize: number): string {
    const lines = jsonString.split('\n')
    let indentLevel = 0
    const indent = ' '.repeat(indentSize)
    
    return lines.map(line => {
      const trimmed = line.trim()
      
      if (!trimmed) return ''
      
      // Decrease indent for closing braces/brackets
      if (trimmed.startsWith('}') || trimmed.startsWith(']')) {
        indentLevel = Math.max(0, indentLevel - 1)
      }
      
      const formattedLine = indent.repeat(indentLevel) + trimmed
      
      // Increase indent for opening braces/brackets
      if (trimmed.endsWith('{') || trimmed.endsWith('[')) {
        indentLevel++
      }
      
      return formattedLine
    }).join('\n')
  }

  static compact(jsonString: string, indentSize: number = 2): string {
    try {
      const parsed = JSON.parse(jsonString)
      
      // Compact format: single line for simple values, multi-line for complex
      return this.compactStringify(parsed, indentSize)
    } catch (error) {
      const fixed = this.attemptToFix(jsonString)
      if (fixed) {
        try {
          const parsed = JSON.parse(fixed)
          return this.compactStringify(parsed, indentSize)
        } catch {
          return jsonString
        }
      }
      return jsonString
    }
  }

  private static compactStringify(obj: any, indentSize: number, currentIndent: number = 0): string {
    const indent = ' '.repeat(indentSize)
    const currentIndentStr = ' '.repeat(currentIndent)
    
    if (obj === null) return 'null'
    if (typeof obj === 'string') return `"${obj}"`
    if (typeof obj === 'number' || typeof obj === 'boolean') return String(obj)
    
    if (Array.isArray(obj)) {
      if (obj.length === 0) return '[]'
      
      // Check if array contains only simple values
      const hasComplexValues = obj.some(item => 
        typeof item === 'object' && item !== null
      )
      
      if (!hasComplexValues) {
        return `[${obj.map(item => this.compactStringify(item, indentSize)).join(', ')}]`
      }
      
      const items = obj.map(item => 
        currentIndentStr + indent + this.compactStringify(item, indentSize, currentIndent + indentSize)
      )
      return `[\n${items.join(',\n')}\n${currentIndentStr}]`
    }
    
    if (typeof obj === 'object') {
      const keys = Object.keys(obj)
      if (keys.length === 0) return '{}'
      
      const items = keys.map(key => {
        const value = this.compactStringify(obj[key], indentSize, currentIndent + indentSize)
        return `${currentIndentStr}${indent}"${key}": ${value}`
      })
      
      return `{\n${items.join(',\n')}\n${currentIndentStr}}`
    }
    
    return String(obj)
  }

  private static attemptToFix(jsonString: string): string | null {
    let fixed = jsonString.trim()
    
    // Remove trailing commas
    fixed = fixed.replace(/,(\s*[}\]])/g, '$1')
    
    // Fix missing quotes around property names
    fixed = fixed.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":')
    
    // Fix single quotes to double quotes
    fixed = fixed.replace(/'/g, '"')
    
    // Fix unescaped quotes in strings
    fixed = fixed.replace(/"([^"]*)"([^"]*)"([^"]*)"/g, (match, p1, p2, p3) => {
      return `"${p1}${p2.replace(/"/g, '\\"')}${p3}"`
    })
    
    // Try to parse the fixed version
    try {
      JSON.parse(fixed)
      return fixed
    } catch {
      return null
    }
  }

  private static basicFormat(jsonString: string, indentSize: number): string {
    const lines = jsonString.split('\n')
    let indentLevel = 0
    const indent = ' '.repeat(indentSize)
    
    return lines.map(line => {
      const trimmed = line.trim()
      
      if (!trimmed) return ''
      
      // Decrease indent for closing braces/brackets
      if (trimmed.startsWith('}') || trimmed.startsWith(']')) {
        indentLevel = Math.max(0, indentLevel - 1)
      }
      
      const formattedLine = indent.repeat(indentLevel) + trimmed
      
      // Increase indent for opening braces/brackets
      if (trimmed.endsWith('{') || trimmed.endsWith('[')) {
        indentLevel++
      }
      
      return formattedLine
    }).join('\n')
  }

  // Utility methods for different formatting styles
  static formatWithCustomIndent(jsonString: string, indentChar: string = ' ', indentSize: number = 2): string {
    try {
      const parsed = JSON.parse(jsonString)
      const indent = indentChar.repeat(indentSize)
      return JSON.stringify(parsed, null, indent)
    } catch {
      return jsonString
    }
  }

  static formatWithTabs(jsonString: string): string {
    return this.formatWithCustomIndent(jsonString, '\t', 1)
  }

  static formatWithSpaces(jsonString: string, spaces: number = 2): string {
    return this.formatWithCustomIndent(jsonString, ' ', spaces)
  }

  // Method to sort object keys alphabetically
  static formatWithSortedKeys(jsonString: string, indentSize: number = 2): string {
    try {
      const parsed = JSON.parse(jsonString)
      const sorted = this.sortObjectKeys(parsed)
      return JSON.stringify(sorted, null, indentSize)
    } catch {
      return jsonString
    }
  }

  private static sortObjectKeys(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.sortObjectKeys(item))
    }
    
    const sorted: any = {}
    Object.keys(obj)
      .sort()
      .forEach(key => {
        sorted[key] = this.sortObjectKeys(obj[key])
      })
    
    return sorted
  }

  // Method to remove comments (JSON doesn't support comments, but some files have them)
  static removeComments(jsonString: string): string {
    return jsonString
      .replace(/\/\*[\s\S]*?\*\//g, '') // Remove /* ... */ comments
      .replace(/\/\/.*$/gm, '') // Remove // comments
      .replace(/^\s*[\r\n]/gm, '') // Remove empty lines
  }

  // Method to validate and format in one step
  static validateAndFormat(jsonString: string, indentSize: number = 2): {
    isValid: boolean
    formatted: string
    error?: string
  } {
    try {
      const parsed = JSON.parse(jsonString)
      return {
        isValid: true,
        formatted: JSON.stringify(parsed, null, indentSize)
      }
    } catch (error) {
      return {
        isValid: false,
        formatted: jsonString,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
}
